// /demo: the workbench on GimasiLM 1.2.0 demo data, no sign-in, no backend.
// Decisions change the page's own state only.
import { useState } from "preact/hooks";
import { Workbench } from "../workbench/Workbench";
import { t } from "../i18n";
import { demoData, type Actions, type GeoFamily, type GeoSuggestion, type LogicModule } from "../workbench/model";
import type { Example, Hints, ProjectDocument, ReviewItem, SampleResult, SizeReport } from "../lib/types";

const DEMO_FILES: Record<string, string> = {
  "manifest.yaml": "format: 1\nid: ch.gimasi.lm\nname: GimasiLM level meter\nvendor: Gimasi SA\nversion: 1.3.1\ncodec: protobuf\nprotocol: glm-session\nengine: \">=0.7.4\"\nvocabulary: \">=1.1.0\"\nprotobuf:\n  root: glm.v1.Uplink\n  files: [proto/glm.proto]\nchannel: stable\nvisibility: public\n",
  "ai_guide.md": "# GimasiLM - guide for AI tools\n\nGimasiLM measures how full the waste tank of a chemical toilet is and how often its door is opened.\n",
};

const DEMO_EXAMPLES: Example[] = [
  { name: "measurement", hex: "01 00 09 29 37 0e 10", origin: "person", expected: {
    state: { probe_ok: true, battery_mv: 3600, temperature: 23.45, humidity: 55 },
    timeseries: [{ key: "battery_mv", value: 3600 }, { key: "temperature", value: 23.45 }, { key: "humidity", value: 55 }] } },
  { name: "alarm_high", hex: "02 01 01 0b b8 00 3c", origin: "person", received_at: "2026-10-06T10:00:00Z", topic: "ts1/0001/up", expected: {
    events: [{ kind: "alarm", ts: "2026-10-06T09:59:00Z", body: { code: "TEMP_HIGH", raised: true, temperature: 30 } }] } },
  { name: "edge_truncated", hex: "01 00 09", origin: "ai", note: "a measurement frame cut after 3 bytes", expected: { errors: ["measurement frame needs 7 bytes, got 3"] } },
];

// logic type (GIOTHUB-31, ?scenario=logic): the VST sensor with the GDP
// logic module; result documents from the module's descriptor
const GDP_SEQ = { v: 1, module: "ch.gimasi.logic.gdp@0.2.0", device_id: "78af58434ff3309e",
  frame: { fport: 2, fcnt: 14, cmd: 2, kind: "sequence", received_at: "2026-10-15T07:37:38.87Z" }, use_case: "TSI",
  samples: [{ ts: "2026-10-15T07:36:23Z", seq: 1070, values: { "0x61": 23.25, "0x62": 23.109375 } },
    { ts: "2026-10-15T07:37:23Z", seq: 1071, values: { "0x61": 23.234375, "0x62": 23.140625 } }] };
const GDP_ERR = { v: 1, module: "ch.gimasi.logic.gdp@0.2.0", device_id: "78af58434ff3309e",
  frame: { fport: 19, fcnt: 5, cmd: 19, kind: "device_error", received_at: "2026-10-15T07:31:41.37Z" }, use_case: "TSI",
  events: [{ ts: "2026-10-15T07:30:42Z", type: "device_error", detail: { class: 1, code: 8, count: 18, detail1: 0, detail2: 1065 } }] };
const DEMO_MODULES: LogicModule[] = [{ id: "ch.gimasi.logic.gdp", name: "GDP (Gimasi Device Protocol, LoRaWAN)", contract_majors: [1],
  description: "Stateful logic for GDP devices (VST sensor): series and sequence tracking, sample expansion with absolute times, gap recovery, acks only for samples the platform stored, bootstrap.",
  connectivity: { downlink: "always" }, params: { properties: {} }, examples: [{ name: "seq", result: GDP_SEQ }, { name: "err", result: GDP_ERR }] }];
const DEMO_EXAMPLES_LOGIC: Example[] = [
  { name: "samples", hex: "", result: GDP_SEQ, origin: "person", received_at: "2026-10-15T07:37:38Z", expected: {
    config: { use_case: "TSI" },
    timeseries: [{ key: "temperature_1", value: 23.25, ts: "2026-10-15T07:36:23Z" }, { key: "temperature_2", value: 23.109375, ts: "2026-10-15T07:36:23Z" },
      { key: "temperature_1", value: 23.234375, ts: "2026-10-15T07:37:23Z" }, { key: "temperature_2", value: 23.140625, ts: "2026-10-15T07:37:23Z" }] } },
  { name: "device_error", hex: "", result: GDP_ERR, origin: "person", expected: {
    config: { use_case: "TSI" }, events: [{ kind: "device_error", ts: "2026-10-15T07:30:42Z", body: { class: 1, code: 8, count: 18, detail1: 0, detail2: 1065 } }] } },
];

const DEMO_EXAMPLES_A: Example[] = [
  { name: "boot_batch0", hex: "08 01 12 0f 38 36 34 31 31 34 30 37 30 37 37 33 35 34 38 18 4c …", origin: "person", from_decoder: true,
    topic: "glm/864114070773548/up", device_serial: "864114070773548", received_at: "2026-10-05T11:00:35Z",
    expected: { state: { "status.battery_pct": { value: 87, ts: "2026-10-05T11:00:30Z" }, "status.battery_mv": { value: 3571, ts: "2026-10-05T11:00:30Z" } } } },
  { name: "records_batch", hex: "08 01 12 0f 38 36 34 …", origin: "person", from_decoder: true, expected: { timeseries: [{ key: "level.distance_mm", value: 1944 }] } },
  { name: "tilt_event", hex: "08 01 12 0f 38 36 34 …", origin: "person", from_decoder: true, expected: { events: [{ kind: "event", body: { type: "EVENT_TILT", raised: true, value: 76 } }] } },
];

// scenario B: a waste bin fill sensor designed in the builder
const B_PROTO = `syntax = "proto3";
package acme.binfill.v1;

// Uplink of the bin fill sensor (topic bins/<device serial>/up), one per hour.
message Uplink {
  uint32 proto_version = 1;      // 1 for this schema
  uint32 batch_seq = 2;          // +1 per uplink, persisted
  TriggerReason trigger = 3;     // why the session started
  StatusRecord status = 4;       // every uplink
  repeated Record records = 5;   // oldest first, max 8
  Config config = 6;             // at boot and after a local change
}
`;

const B_REQUIREMENTS: ProjectDocument = {
  name: "requirements", version: 3, status: "confirmed", author_kind: "ai", updated_at: "2026-10-10T15:20:00Z",
  confirmed_version: 3, confirmed_at: "2026-10-10T15:31:00Z", confirmed_by: "massimo",
  content: `# Bin fill sensor - requirements

## Device
An ultrasonic fill level sensor mounted under the lid of a 240 l public waste bin.

## Measurements
| Quantity | Unit | Range | Resolution | Interval |
| --- | --- | --- | --- | --- |
| Distance to the surface | mm | 100 - 1500 | 5 mm | 15 min |
| Fill level | % | 0 - 100 | 1 % | 15 min (from the distance and the bin depth) |
| Battery voltage | mV | 2000 - 3700 | 10 mV | every report |
| Lid temperature | 0.1 degC | -30 - 70 degC | 0.1 degC | every report (fire detection) |

## Events and alarms
- **Full**: fill level at or above \`full_threshold_pct\` (default 80 %) on 2 measurements in a row; cleared below it minus 10 %.
- **Fire**: temperature above 65 degC; sent at once.
- **Tilt**: bin knocked over (accelerometer, > 45 deg for 30 s).

## Configuration
| Setting | Unit | Default | Range |
| --- | --- | --- | --- |
| measure_interval_s | s | 900 | 300 - 3600 |
| report_interval_s | s | 3600 | 900 - 86400 |
| full_threshold_pct | % | 80 | 50 - 95 |
| bin_depth_mm | mm | 1100 | 300 - 1500 |

## Commands
Reboot, measure now, send the configuration.

## Reporting
One uplink per hour with the status and up to 8 level records; at boot with the full configuration.

## Radio and power
LTE-M, MQTT over TCP; payload limit 1024 bytes per uplink. 2 x AA lithium (3000 mAh), target 5 years.

## Time
NTP over LTE at every session.

## Open points
- None (bin depth confirmed per installation, configurable).
`,
};

const B_SIZES: SizeReport = {
  root: "acme.binfill.v1.Uplink",
  messages: [
    { message: "acme.binfill.v1.Uplink", max_bytes: 412, bounded: true },
    { message: "acme.binfill.v1.Config", max_bytes: 30, bounded: true },
    { message: "acme.binfill.v1.LevelRecord", max_bytes: 22, bounded: true },
    { message: "acme.binfill.v1.Record", max_bytes: 41, bounded: true },
    { message: "acme.binfill.v1.StatusRecord", max_bytes: 58, bounded: true },
  ],
};

function demoItemsB(): ReviewItem[] {
  const k = (field: string, keyPath: string, cls: string[], unit: string, role: string, q: Record<string, string>, label: string,
    confidence: "high" | "medium" | "low", state: ReviewItem["state"], reason: string): ReviewItem => ({
    id: `b-${keyPath}`, kind: "key", key_path: keyPath, state, confidence, reason, question: "", answer: "", blocking: false,
    proposal: { field: "acme.binfill.v1." + field, key: keyPath, hints: { class: cls, unit: unit || undefined, role: role || undefined, qualifiers: q, label } },
  });
  const TS = ["STATE", "TIMESERIES"];
  return [
    k("LevelRecord.distance_mm", "level.distance_mm", ["TIMESERIES"], "mm", "distance", { of: "surface" }, "Distance to the surface", "high", "accepted", "requirements: distance in mm"),
    k("LevelRecord.fill_pct", "level.fill_pct", ["TIMESERIES"], "%", "fill_level", { of: "bin" }, "Fill level", "high", "accepted", "requirements: fill level %"),
    k("StatusRecord.battery_mv", "status.battery_mv", TS, "mV", "voltage", { of: "battery" }, "Battery voltage", "high", "proposed", "unit in the name"),
    k("StatusRecord.temperature_dc", "status.temperature_dc", TS, "0.1.Cel", "temperature", { of: "device" }, "Lid temperature", "high", "proposed", "requirements: 0.1 degC"),
    k("StatusRecord.tilted", "status.tilted", TS, "", "", {}, "Knocked over", "medium", "proposed", "requirements: tilt alarm"),
    k("Config.full_threshold_pct", "full_threshold_pct", ["CONFIG"], "%", "", {}, "Full threshold", "high", "proposed", "requirements: configuration"),
  ];
}

const DEMO_RESULTS: SampleResult[] = [
  { name: "measurement", ok: true, duration_ms: 0.41 },
  { name: "alarm_high", ok: false, duration_ms: 0.38, diff: ["$.events[0].body.temperature: expected 30, got 3000"],
    got: { events: [{ kind: "alarm", ts: "2026-10-06T09:59:00Z", time_valid: true, body: { code: "TEMP_HIGH", raised: true, temperature: 3000 } }] } },
  { name: "edge_truncated", ok: true, duration_ms: 0.12 },
];

// geo meaning tags (GIOTHUB-64, &geo=1): the picker on two location fields,
// one of them with a German name, and suggestions as the builder makes them
const DEMO_GEO: GeoFamily[] = [
  { family: "gnss", label: "GNSS", parts: [["lat", "Latitude", "deg"], ["lon", "Longitude", "deg"], ["valid", "Fix is valid"], ["fix", "Fix type"],
    ["alt", "Altitude", "m"], ["accuracy", "Horizontal accuracy", "m"], ["hdop", "HDOP"], ["sats_used", "Satellites used"], ["speed", "Ground speed", "km/h"],
    ["heading", "Course over ground", "deg"], ["ttff", "Time to first fix", "s"]].map(([p, l, u]) => ({ tag: "gnss." + p, label: l!, unit: u })) },
  { family: "wifi", label: "Wi-Fi", parts: [["bssid", "Access point MAC (BSSID)"], ["rssi", "Signal (RSSI)", "dB[mW]"], ["channel", "Channel"]]
    .map(([p, l, u]) => ({ tag: "wifi." + p, label: l!, unit: u })) },
  { family: "cell", label: "Cell", parts: [["rat", "Radio technology"], ["mcc", "Country code (MCC)"], ["mnc", "Network code (MNC)"], ["area", "Area (LAC / TAC)"],
    ["cell_id", "Cell id"], ["pci", "Physical cell id (PCI)"], ["rsrp", "RSRP", "dB[mW]"]].map(([p, l, u]) => ({ tag: "cell." + p, label: l!, unit: u })) },
  { family: "ble", label: "BLE", parts: [["mac", "Advertiser MAC"], ["uuid", "iBeacon UUID"], ["rssi", "Signal (RSSI)", "dB[mW]"]]
    .map(([p, l, u]) => ({ tag: "ble." + p, label: l!, unit: u })) },
];
const DEMO_GEO_SUGGEST: Record<string, GeoSuggestion[]> = {
  "glm.v1.LocationRecord.satellites": [{ tag: "gnss.sats_used", label: "Satellites used", reason: 'name "satellites", GNSS context' }],
  "acme.v1.Position.breitengrad": [{ tag: "gnss.lat", label: "Latitude", reason: 'name "breitengrad", GNSS context' }],
};

export function DemoWorkbench() {
  const scen = new URLSearchParams(location.search).get("scenario");
  const logic = scen === "logic";
  const c = scen === "c" || logic;
  const b = scen === "b";
  const [data, setData] = useState(() => {
    const d = demoData();
    if (b) {
      d.project = { ...d.project, scenario: "B", type_id: "com.acme.binfill", name: "Bin fill sensor", head_rev: 14, publish_target: "git" };
      d.files = ["manifest.yaml", "proto/binfill.proto", "classification.yaml", "catalogue.json", "ai_guide.md", "samples/gen_boot.bin",
        "samples/gen_hourly.bin", "samples/gen_full_alarm.bin", "CHANGELOG.md"].map((path) => ({ path, sha256: "", size: 0 }));
      d.requirements = B_REQUIREMENTS;
      d.validation = d.validation && { ...d.validation, rev: 14, warnings: 1, samples_ok: 6, samples_total: 6, report: { findings: [
        { level: "warning", code: "listing.website", path: "manifest.yaml#listing.website", message: "no website" },
        { level: "info", code: "proto.compiled", path: "proto/binfill.proto", message: "Compiled: 7 messages, root acme.binfill.v1.Uplink." }] } };
      d.items = demoItemsB();
      d.inputs = [{ id: "b1", name: "BinFill schematic rev A.pdf", content_type: "application/pdf", size: 640_000, created_at: "2026-10-10T15:00:00Z" },
        { id: "b2", name: "MaxBotix MB7389 datasheet.pdf", content_type: "application/pdf", size: 1_200_000, created_at: "2026-10-10T15:00:00Z" }];
      d.turns = [
        { id: 1, role: "user", created_at: "", content: [{ type: "text", text: "This is the device: a fill level sensor for public waste bins…" }] },
        { id: 2, role: "assistant", created_at: "", content: [{ type: "text", text: "I wrote the requirements (version 1) from your description and the MB7389 datasheet. Three open points are questions for you: the bin depth, the full threshold and the payload limit of your LTE-M plan." }] },
        { id: 3, role: "user", created_at: "", content: [{ type: "text", text: "The requirements are confirmed. Design the protocol…" }] },
        { id: 4, role: "assistant", created_at: "", content: [{ type: "text", text: "The protocol is in proto/binfill.proto: Uplink with StatusRecord, up to 8 Records (level, alert) and Config. With the nanopb limits the largest uplink is 412 bytes, within your 1024-byte limit. I classified 31 fields and generated 6 samples; all pass." }] },
      ];
    }
    if (c) {
      d.project = { ...d.project, scenario: "C", type_id: "com.example.tempsensor", name: "TS-1 temperature logger", head_rev: 7 };
      d.files = ["manifest.yaml", "decode.js", "catalogue.json", "ai_guide.md", "samples/measurement.hex", "samples/alarm_high.hex", "CHANGELOG.md"]
        .map((path) => ({ path, sha256: "", size: 0 }));
      d.inputs = [{ id: "c1", name: "TS-1 payload format.pdf", content_type: "application/pdf", size: 412_000, created_at: "2026-10-10T16:00:00Z" }];
      const e = (key: string, cls: string[], type: string, unit: string, role: string, label: string, state: ReviewItem["state"], reason: string): ReviewItem => ({
        id: `c-${key}`, kind: "key", key_path: key, state, confidence: "high", reason, question: "", answer: "", blocking: false,
        proposal: { key, hints: { classes: cls, type, unit: unit || undefined, role: role || undefined, label } },
      });
      d.items = [
        e("temperature", ["STATE", "TIMESERIES"], "number", "Cel", "temperature", "Temperature", "accepted", "frame 0x01 bytes 2-3, 0.01 degC"),
        e("humidity", ["STATE", "TIMESERIES"], "integer", "%", "humidity", "Relative humidity", "accepted", "frame 0x01 byte 4"),
        e("battery_mv", ["STATE", "TIMESERIES"], "integer", "mV", "voltage", "Battery voltage", "proposed", "frame 0x01 bytes 5-6"),
        e("probe_ok", ["STATE"], "boolean", "", "status", "Probe working", "proposed", "frame 0x01 byte 1 bit 0"),
        e("alarm", ["EVENT"], "object", "", "alarm", "Alarm", "proposed", "frame 0x02"),
        { id: "c-q1", kind: "question", key_path: "alarm", state: "open", proposal: { hints: {} }, confidence: null, reason: "", blocking: true, answer: "",
          question: "The document names alarm codes 1 high, 2 low, 3 tamper; your example shows only 1 = TEMP_HIGH. Are TEMP_LOW and TAMPER the right names for 2 and 3?" },
      ];
      d.turns = [
        { id: 1, role: "user", created_at: "", content: [{ type: "text", text: "Write the JavaScript decoder of this device type from the uploaded documents and my examples." }] },
        { id: 2, role: "assistant", created_at: "", content: [
          { type: "tool_use", id: "c1", name: "list_examples" }, { type: "tool_use", id: "c2", name: "read_document" },
          { type: "tool_use", id: "c3", name: "write_file" }, { type: "tool_use", id: "c4", name: "propose_keys" },
          { type: "tool_use", id: "c5", name: "add_example" }, { type: "tool_use", id: "c6", name: "run_samples" }] },
        { id: 3, role: "user", created_at: "", content: [
          { type: "tool_result", tool_use_id: "c1", _summary: "2 examples (2 by the person) · 0.0 s" },
          { type: "tool_result", tool_use_id: "c2", _summary: "TS-1 payload format.pdf · 402 kB · 6.1 s" },
          { type: "tool_result", tool_use_id: "c3", _summary: "decode.js · r6 · 0.0 s" },
          { type: "tool_result", tool_use_id: "c4", _summary: "5 proposals stored, 0 refused · 0.1 s" },
          { type: "tool_result", tool_use_id: "c5", _summary: "example edge_truncated · r7 · 0.0 s" },
          { type: "tool_result", tool_use_id: "c6", _summary: "2 of 3 examples pass (1 of the person's fail) · 0.0 s" }] },
        { id: 4, role: "assistant", created_at: "", content: [{ type: "text", text: "decode.js decodes measurement frames and the truncated edge case. alarm_high still fails: your example has 30 degC where the document's 0.01 degC scaling gives 3000 - one question for you before I change it." }] },
      ];
      d.validation = d.validation && { ...d.validation, rev: 7, errors: 0, warnings: 1, samples_ok: 2, samples_total: 3, report: { findings: [
        { level: "warning", code: "samples.min", path: "samples", message: "3 samples; the store asks for at least 5." },
        { level: "info", code: "js.compiled", path: "decode.js", message: "decodeUplink found; runs in the QuickJS sandbox." }] } };
    }
    if (logic) {
      d.project = { ...d.project, type_id: "ch.gimasi.vst", name: "VST sensor (GDP)", head_rev: 9 };
      d.files = ["manifest.yaml", "decode.js", "catalogue.json", "ai_guide.md", "samples/samples.bin", "samples/device_error.bin", "CHANGELOG.md"]
        .map((path) => ({ path, sha256: "", size: 0 }));
      d.logicTypes = true;
      d.modules = DEMO_MODULES;
      d.logic = { module: "ch.gimasi.logic.gdp", version: "^1" };
      d.inputs = [{ id: "l1", name: "GDP_LoRaWAN_protocol_ver_2_7.pdf", content_type: "application/pdf", size: 980_000, created_at: "2026-10-15T08:00:00Z" }];
      const e = (key: string, hints: ReviewItem["proposal"]["hints"], state: ReviewItem["state"], reason: string): ReviewItem => ({
        id: `l-${key}`, kind: "key", key_path: key, state, confidence: "high", reason, question: "", answer: "", blocking: false, proposal: { key, hints } });
      d.items = [
        e("use_case", { classes: ["CONFIG"], type: "enum", label: "Use case", default: "TSI", onboarding: "required",
          confirm: "Changing the use case reconfigures the sensor and starts a new measurement series." }, "proposed", "GDP 2.7: BOOTSTRAP_CONF_ID; module config key"),
        e("temperature_1", { classes: ["TIMESERIES"], type: "number", unit: "Cel", role: "temperature", qualifiers: { of: "ambient", channel: "1" }, label: "Temperature 1" }, "accepted", "TSI: sensor 0x61"),
        e("temperature_2", { classes: ["TIMESERIES"], type: "number", unit: "Cel", role: "temperature", qualifiers: { of: "ambient", channel: "2" }, label: "Temperature 2" }, "accepted", "TSI: sensor 0x62"),
        e("device_error", { classes: ["EVENT"], type: "object", label: "Device error" }, "proposed", "module event device_error"),
      ];
      d.turns = [
        { id: 1, role: "user", created_at: "", content: [{ type: "text", text: "Write the decoder of this logic type from the module's result documents and my examples." }] },
        { id: 2, role: "assistant", created_at: "", content: [{ type: "text", text: "decode.js maps the GDP module's result documents: samples to `temperature_1` / `temperature_2` for use case TSI, device errors to events. It keeps no state: sequences and acknowledgements stay in the module. Both of your examples pass; `use_case` is proposed with onboarding required and a confirmation text." }] },
      ];
      d.validation = d.validation && { ...d.validation, rev: 9, engine: "0.9.0", errors: 0, warnings: 1, samples_ok: 2, samples_total: 2, report: { findings: [
        { level: "warning", code: "samples.min", path: "samples", message: "2 samples; the store asks for at least 5." }] } };
      if (new URLSearchParams(location.search).get("view") === "setup") d.setup = { scenario: "C", protoFiles: [], messages: [], error: "" };
    }
    if (new URLSearchParams(location.search).get("geo") === "1") {
      d.geoTags = true;
      d.geoFamilies = DEMO_GEO;
      d.geolocation = { enabled: true, inputs: ["wifi", "cell"], reported: true, uere_m: 5 };
      const geoKey = (field: string, key: string, hints: Hints, reason: string): ReviewItem => ({
        id: `g-${key}`, kind: "key", key_path: key, state: "proposed", confidence: "high", reason, question: "", answer: "", blocking: false, proposal: { field, key, hints } });
      d.items = [
        geoKey("glm.v1.LocationRecord.satellites", "location.satellites", { class: ["STATE"], label: "Satellites used", geo: "gnss.sats_used" },
          "Satellites used for the fix: tagged gnss.sats_used so the position carries it."),
        geoKey("acme.v1.Position.breitengrad", "pos.breitengrad", { class: ["STATE"], scale: 0.000001, label: "Latitude" },
          "German name: Breitengrad = latitude, degrees x 1e6."),
        ...d.items,
      ];
    }
    return d;
  });
  const update = (fn: (d: ReturnType<typeof demoData>) => void) =>
    setData((d) => {
      const n = { ...d, items: d.items.map((i) => ({ ...i })), turns: [...d.turns] };
      fn(n);
      n.summary = { states: {}, blocking: n.items.filter((i) => i.blocking && (i.state === "open" || i.state === "proposed")).length };
      return n;
    });
  const actions: Actions = {
    async decide(id, action, answer) {
      update((d) => {
        const it = d.items.find((i) => i.id === id);
        if (!it) return;
        it.state = action === "accept" || action === "adopt" ? "accepted" : action === "reject" ? "rejected"
          : action === "answer" ? "answered" : "dismissed";
        if (answer) it.answer = answer;
      });
    },
    async acceptHigh(keys) {
      update((d) => d.items.forEach((i) => { if (keys.includes(i.key_path) && i.kind === "key") i.state = "accepted"; }));
    },
    async validate() {},
    async send(text) {
      update((d) => {
        d.turns.push({ id: d.turns.length + 1, role: "user", created_at: "", content: [{ type: "text", text }] });
        d.turns.push({ id: d.turns.length + 1, role: "assistant", created_at: "", content: [{ type: "text", text: t("proj.demo.reply") }] });
      });
    },
    async uploadSources() {},
    async uploadInputs() {},
    async setup() {},
    async applyReview() {},
    async edit(id, proposal) {
      update((d) => {
        const it = d.items.find((i) => i.id === id);
        if (it) {
          it.state = "edited";
          it.edited = proposal;
        }
      });
    },
    async readFile(path) {
      if (path === "proto/binfill.proto") return B_PROTO;
      return DEMO_FILES[path] ?? t("proj.demo.file", { path });
    },
    async saveFile() {},
    async geoSuggest(field) {
      return DEMO_GEO_SUGGEST[field] ?? [];
    },
    async fieldInfo(field) {
      const name = field.split(".").pop() ?? field;
      return { full_name: field, name, number: 0, type: name.endsWith("_dbm") ? "sint32" : "uint32", cardinality: "singular",
        trailing_comment: t("proj.demo.comment"), effective: {} };
    },
    async vocabulary() {
      return null;
    },
    async versions() {
      if (b) return [];
      return [{ project_id: "demo", version: "1.2.0", rev: 31, target: "store", status: "published", external_ref: "store:ch.gimasi.lm@1.2.0",
        created_at: "2026-10-01T10:00:00Z", ai_review: { verdict: "ready", summary: "Labels, units and meanings are consistent; the guide covers level, usage and health.", findings: [] } }];
    },
    async setPublishing() {},
    async newDeployKey() {
      return { public_key: "ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIDemoDemoDemoDemoDemoDemoDemoDemoDemoDemo giothub-builder-demo", key: "" };
    },
    async createVersion() {},
    async reviewVersion() {},
    async publishVersion() {},
    async refreshVersion() {},
    async examples() {
      return logic ? DEMO_EXAMPLES_LOGIC : c ? DEMO_EXAMPLES : DEMO_EXAMPLES_A;
    },
    async putExample() {},
    async deleteExample() {},
    async revisions() {
      return [
        { project_id: "demo", rev: 37, parent_rev: 36, author_kind: "person" as const, author_id: "massimo", message: "Review applied: 12 key(s)", created_at: "2026-10-10T14:02:00Z" },
        { project_id: "demo", rev: 36, parent_rev: 35, author_kind: "ai" as const, author_id: "massimo", message: "AI: ai_guide.md", created_at: "2026-10-10T13:58:00Z" },
      ];
    },
    async document(name) {
      return name === "requirements" ? data.requirements ?? B_REQUIREMENTS
        : { name, version: 2, status: "draft", author_kind: "ai", content: "acme.binfill.v1.Uplink.records max_count:8\nacme.binfill.v1.StatusRecord.fw_version max_size:16\n" };
    },
    async putDocument(name, content, version) {
      return { name, content, version: version + 1, status: "draft", author_kind: "person" };
    },
    async confirmRequirements() {},
    async sizes() {
      return b ? B_SIZES : null;
    },
    async firmwareZip() {
      return new Blob([B_PROTO], { type: "text/plain" });
    },
    async runExamples() {
      return logic ? DEMO_EXAMPLES_LOGIC.map((e) => ({ name: e.name, ok: true, duration_ms: 0.3 })) : c ? DEMO_RESULTS : DEMO_EXAMPLES_A.map((e) => ({ name: e.name, ok: true, duration_ms: 0.2 }));
    },
  };
  return <Workbench data={data} actions={actions} />;
}
